import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useAddressingContextChannelId,
  useAddressingContextMediaId,
  useAddressingContextWorkgroupId,
} from '../context/hooks';
import { AddressingActions, AddressingCleanUp, AddressingSelectors } from '../duck';
import { generateTreeIdentifier } from '../duck/utils';
import { AddressingTree, AddressingTreeProps } from './AddressingTree';

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

// AddressingComponentProps is similar to AddressingTreeProps, except for making the wk, channel and media optional
type AddressingComponentProps = PartialBy<AddressingTreeProps, 'workgroupId' | 'channelId' | 'mediaId'> & {
  cleanupMode: AddressingCleanUp;
};

export const AddressingComponent: React.FunctionComponent<AddressingComponentProps> = (props) => {
  const dispatch = useDispatch();
  const contextWorkgrouplId = useAddressingContextWorkgroupId();
  const contextChannelId = useAddressingContextChannelId();
  const contextMediaId = useAddressingContextMediaId();
  const workgroupId = props.workgroupId ? props.workgroupId : contextWorkgrouplId;
  const channelId = props.channelId ? props.channelId : contextChannelId;
  const mediaId = props.mediaId ? props.mediaId : contextMediaId;
  const cleanUpRef = useRef(props.cleanupMode);
  cleanUpRef.current = props.cleanupMode;
  const initialWorkgroupId = useRef(workgroupId);
  const initialChannelId = useRef(channelId);
  const initialMediaId = useRef(mediaId);
  const treeId = generateTreeIdentifier(workgroupId, channelId);
  const initialTreeId = useRef(treeId);
  const initializationComplete = useSelector(AddressingSelectors.selectInitializationComplete(treeId, mediaId));
  const addressingIdRef = useRef(treeId);
  addressingIdRef.current = treeId;

  const cleanUpOldIstance = useCallback(
    (entity: string, refToUpdate: React.MutableRefObject<number>, updatedValue: number) => {
      if (refToUpdate.current === updatedValue) {
        return;
      }

      console.info(
        `AddressingComponent ${entity}: clearAddressingInstace treeId ${initialTreeId.current} mediaId ${initialMediaId.current}`
      );
      dispatch(
        AddressingActions.clearAddressingInstance([{ treeId: initialTreeId.current, mediaId: initialMediaId.current }])
      );

      refToUpdate.current = updatedValue;
      initialTreeId.current = addressingIdRef.current;
    },
    [dispatch]
  );

  // we need to initialize the addressingInstance here
  useEffect(() => {
    if (workgroupId && channelId) {
      dispatch(AddressingActions.initAddressingStructure(treeId, workgroupId, channelId));
      dispatch(AddressingActions.initAddressingInstance(treeId, mediaId));
    }
  }, [treeId, workgroupId, channelId, mediaId, dispatch]);

  // do cleanup when this component unmounts
  useEffect(() => {
    return () => {
      // because we are using a ref this condition will always query the most recent value of cleanUpMode
      if (cleanUpRef.current & AddressingCleanUp.ON_UNMOUNT) {
        console.info(`AddressingComponent UNMOUNT: clearAddressingFull ${addressingIdRef.current}`);
        dispatch(AddressingActions.clearAddressingInstance([{ treeId: addressingIdRef.current, mediaId: mediaId }]));
      }
    };
  }, [dispatch]);

  // do cleanup when workgroup changes
  useEffect(() => {
    if (workgroupId !== initialWorkgroupId.current && props.cleanupMode & AddressingCleanUp.ON_WORKGROUP_CHANGE) {
      cleanUpOldIstance('WORKGROUP_CHANGE', initialWorkgroupId, workgroupId);
    }
  }, [props.cleanupMode, workgroupId, cleanUpOldIstance]);

  // do cleanup when channel changes
  useEffect(() => {
    if (channelId !== initialChannelId.current && props.cleanupMode & AddressingCleanUp.ON_CHANNEL_CHANGE) {
      cleanUpOldIstance('CHANNEL_CHANGE', initialChannelId, channelId);
    }
  }, [props.cleanupMode, channelId, cleanUpOldIstance]);

  // do cleanup when media changes
  useEffect(() => {
    if (mediaId !== initialMediaId.current && props.cleanupMode & AddressingCleanUp.ON_MEDIA_CHANGE) {
      cleanUpOldIstance('MEDIA_CHANGE', initialMediaId, mediaId);
    }
  }, [props.cleanupMode, mediaId, cleanUpOldIstance]);

  if (initializationComplete) {
    return <AddressingTree {...props} workgroupId={workgroupId} channelId={channelId} mediaId={mediaId} />;
  } else {
    return props.placeholder;
  }
};

export default AddressingComponent;
